import { useEffect } from 'react';

import AnalyticsService, { CalledFromValues, EventNameAMP, ParamNameAMP } from 'application/services/AnalyticsService';
import {
  PointParamNameAMP,
  StreamBindFlowEventNameAMP,
  StreamBindFlowParamNameAMP,
} from 'application/services/AnalyticsService/ampTypes';
import { CurrentPointOnboardingType, DeviceTypesInFlow } from 'application/components/common/SelectPlayerFlow/types';
import { ModalProps, ModalTypes } from 'application/providers/ModalProvider';

import { SelectedPoint, StreamBindSuccessModalType, UseStreamBindSuccessModalViewModel } from './types';

interface Props {
  selectedPointsProps: SelectedPoint[];
  currentPoint: CurrentPointOnboardingType;
  redirectToPointsPage: () => void;
  close: (count?: number) => void;
  successCallback?: () => void;
  replaceModal: <T extends ModalTypes>(
    type: T,
    modalState: Omit<ModalProps[T], 'close' | 'open' | 'openModal' | 'replaceModal'>,
    onModalCloseCallback?: () => void
  ) => void;
  clearCurrentPoint: () => void;
}

const useStreamBindSuccessModalViewModel = ({
  selectedPointsProps,
  currentPoint,
  redirectToPointsPage,
  close,
  successCallback,
  replaceModal,
  clearCurrentPoint,
}: Props): UseStreamBindSuccessModalViewModel => {
  const selectedPoints: SelectedPoint[] =
    selectedPointsProps.length !== 0
      ? selectedPointsProps.map((point) => ({
          id: point.id,
          address: point.address,
          title: point.title,
          device: {
            type: point.device?.type as unknown as DeviceTypesInFlow,
            requiredVersionUrl: point.device?.requiredVersionUrl,
          },
          isEnabled: point.isEnabled,
        }))
      : [
          {
            id: currentPoint?.id || '',
            address: currentPoint.address,
            title: currentPoint.address,
            device: {
              type: currentPoint.device?.type as unknown as DeviceTypesInFlow,
              requiredVersionUrl: currentPoint.device?.requiredVersionUrl,
            },
            isEnabled: currentPoint.isEnabled,
          },
        ];

  const showWebPlayerButton =
    selectedPoints.length === 1 &&
    selectedPoints[0].device?.type === DeviceTypesInFlow.WEBPLAYER &&
    selectedPoints[0].isEnabled === true &&
    Boolean(selectedPoints[0].device?.requiredVersionUrl);

  const isAtLeastOnePointWithoutDevice = selectedPoints.some((point) => !point.device?.type);

  const onClose = () => {
    clearCurrentPoint();
    successCallback?.();
    close();
  };

  const onShowPointsPage = () => {
    redirectToPointsPage();
    onClose();
  };

  const onShowWebPlayerClick = () => {
    AnalyticsService.event(EventNameAMP.OPEN_WEBPLAYER_PRESSED, {
      [PointParamNameAMP.POINT_ID]: selectedPoints[0].id,
      [ParamNameAMP.CALLED_FROM]: CalledFromValues.STREAM_BC_SUCCESS,
    });

    if (currentPoint.isEnabled === false) {
      replaceModal(ModalTypes.PAYMENT_REQUIREMENT, {});
    } else {
      setTimeout(() => {
        window.open(selectedPoints[0]?.device?.requiredVersionUrl!);
      }, 100);

      onClose();
    }
  };

  useEffect(() => {
    let successModalType = StreamBindSuccessModalType.COMPLETED;

    if (showWebPlayerButton) {
      successModalType = StreamBindSuccessModalType.OPEN_WEBPLAYER;
    }

    AnalyticsService.event(StreamBindFlowEventNameAMP.STREAM_BIND_SUCCESS_MODAL_SHOWED, {
      [StreamBindFlowParamNameAMP.SUCCESS_MODAL_TYPE]: successModalType,
    });

    return () => {
      clearCurrentPoint();
    };
  }, []);

  return {
    selectedPoints,
    showWebPlayerButton,
    isAtLeastOnePointWithoutDevice,
    onShowWebPlayerClick,
    onShowPointsPage,
    onClose,
  };
};

export default useStreamBindSuccessModalViewModel;
